// src/context/BrandingContext.jsx
// Dynamic branding context — fetches user branding settings and injects CSS variables
import React, { createContext, useContext, useEffect, useState } from 'react';

const BrandingContext = createContext({
  logoUrl: null,
  primaryColor: '#FFB800',
  accentColor: '#0B1120',
  companyName: '',
  extractedColors: [],
  allowCustom: true,
  agencyBranding: null,
  reload: () => {},
});

export function useBranding() {
  return useContext(BrandingContext);
}

export function BrandingProvider({ children }) {
  const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
  const [branding, setBranding] = useState({
    logoUrl: null,
    primaryColor: '#FFB800',
    accentColor: '#0B1120',
    companyName: '',
    extractedColors: [],
    allowCustom: true,
    agencyBranding: null,
  });

  const fetchBranding = async () => {
    try {
      const [userRes, agencyRes] = await Promise.all([
        fetch(`${BASE}/api/branding`, { credentials: 'include' }),
        fetch(`${BASE}/api/branding/agency`, { credentials: 'include' }),
      ]);

      const userJson = userRes.ok ? await userRes.json() : {};
      const agencyJson = agencyRes.ok ? await agencyRes.json() : {};

      const userBranding = userJson.branding || {};
      const agencyBranding = agencyJson.agencyBranding || null;
      const allowCustom = agencyJson.allowCustom !== false;

      const primaryColor = userBranding.primaryColor || agencyBranding?.primary_color || '#FFB800';
      const accentColor = userBranding.accentColor || agencyBranding?.accent_color || '#0B1120';
      const logoUrl = userBranding.logoUrl || agencyBranding?.logo_url || null;

      // Inject CSS variables
      document.documentElement.style.setProperty('--brand-primary', primaryColor);
      document.documentElement.style.setProperty('--brand-accent', accentColor);

      setBranding({
        logoUrl,
        primaryColor,
        accentColor,
        companyName: userBranding.companyName || '',
        extractedColors: userBranding.extractedColors || [],
        allowCustom,
        agencyBranding,
      });
    } catch (err) {
      // Silent fail — branding is non-critical, use defaults
      console.warn('[BrandingContext] Failed to load branding:', err.message);
    }
  };

  useEffect(() => {
    fetchBranding();
  }, []);

  return (
    <BrandingContext.Provider value={{ ...branding, reload: fetchBranding }}>
      {children}
    </BrandingContext.Provider>
  );
}
